import {useEffect, useRef, useState} from 'react'
import services from '../../utils/services'
import {useAppSelector} from '../../stores/configureStore'
import css from '../../css/shareButton.module.css'
import {Config} from '../../constants/Config'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faShareAlt} from '@fortawesome/free-solid-svg-icons'
import {useMediaQuery} from 'react-responsive'
import {Page} from '../../models/editor/page'
import {
  getPrintAreaClientRect,
  getPrintAreaItemsClientRects,
} from '../../utils/helpers/frontendEditor'
import {calculatePrice} from '../../utils/helpers/price'
import {ProductPricing} from '../../models/dto/common/productPricing'
import {env} from 'process'
import {ProductColor} from '../../models/dto/common/productColor'
import {useTranslation} from 'react-i18next'

interface ShareButtonProps {
  productId: number
  slug: string
  pages: Page[]
  selectedPageIndex: number | undefined
  productPricing: ProductPricing | null
  calendar: boolean
  selectedColor: ProductColor | undefined
  selectedSize: string | undefined
}

export default function ShareButton({
  productId,
  slug,
  pages,
  selectedPageIndex,
  productPricing,
  calendar,
  selectedColor,
  selectedSize,
}: ShareButtonProps) {
  const {t, i18n} = useTranslation()
  const editorStore = useAppSelector(store => store.editor)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isPanelVisible, setIsPanelVisible] = useState<boolean>(false)
  const [shareUrl, setShareUrl] = useState<string>('')
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (isPanelVisible && ref.current && e.target instanceof HTMLElement) {
        const isClickInside = ref.current?.contains(e.target)

        if (!isClickInside) {
          setIsPanelVisible(false)
        }
      }
    }

    window.addEventListener('mousedown', handleClick)

    return () => {
      window.removeEventListener('mousedown', handleClick)
    }
  }, [ref, setIsPanelVisible, isPanelVisible])

  const handleClick = async () => {
    if (isPanelVisible) {
      setIsPanelVisible(false)
      setIsLoading(false)
      setShareUrl('')
      return
    }

    if (productPricing) {
      try {
        setIsLoading(true)
        setIsPanelVisible(!isPanelVisible)

        const response = await services.saveStoreState(productId, {
          pages: pages.map((page, index) =>
            selectedPageIndex === index
              ? {
                  ...page,
                  images: editorStore.images.slice(1),
                  mask: editorStore.maskImage,
                  price:
                    (editorStore.printArea &&
                      page.printAreaNativeWidth &&
                      page.printAreaNativeHeight &&
                      calculatePrice(
                        getPrintAreaClientRect(editorStore.printArea),
                        page.printAreaNativeWidth,
                        page.printAreaNativeHeight,
                        getPrintAreaItemsClientRects(editorStore.images),
                        productPricing,
                        selectedPageIndex,
                        calendar,
                        index === 1,
                        pages.length > 1 && pages[0]?.images?.length > 1,
                      )) ??
                    0,
                  imageClientRects: getPrintAreaItemsClientRects(
                    editorStore.images,
                  ),
                }
              : {...page, images: page.images.slice(1)},
          ),
          color: selectedColor ?? null,
          size: selectedSize ?? null,
        })

        if (env.NODE_ENV === 'development') {
          setShareUrl(
            `http://localhost:3000/polotervezo#frontend/${productId}?sharedId=${response.data}&lang=${i18n.language}`,
          )
        } else {
          setShareUrl(
            `${Config.websiteUrl}/${i18n.language}/termek/${slug}?mentett_polotervezo=${response.data}`,
          )
        }
        setIsLoading(false)
      } catch (err) {
        setIsLoading(false)
        alert(t('Hiba történt, nem sikerült menteni az állapotot.'))
      }
    }
  }
  const isMobile = useMediaQuery({maxWidth: 1024})

  const inputRef = useRef<HTMLInputElement>(null)
  const [copied, setCopied] = useState(false)

  return (
    <div className={css.shareButtonWrapper} ref={ref}>
      <button
        type="button"
        className={['editorButton', isMobile ? css.mobileShareButton : ''].join(
          ' ',
        )}
        onClick={() => handleClick()}>
        {isMobile ? (
          <FontAwesomeIcon icon={faShareAlt} title={t('Megosztás')} />
        ) : (
          t('Megosztás')
        )}
      </button>

      {isPanelVisible && (
        <div className={css.panelWrapper}>
          {isLoading && <p>{t('Betöltés...')}</p>}
          {!isLoading && shareUrl && (
            <>
              <input
                type="text"
                value={shareUrl}
                readOnly={true}
                ref={inputRef}
              />
              {!copied ? (
                <button
                  className={`${css.copyText} ${css.copyButton}`}
                  onClick={() => {
                    navigator.clipboard &&
                      navigator.clipboard.writeText(shareUrl)
                    inputRef.current?.select()
                    setCopied(() => true)
                    setTimeout(() => {
                      setCopied(() => false)
                    }, 2000)
                  }}>
                  {t('Másolás vágólapra')}
                </button>
              ) : (
                <p className={`${css.copyText} ${css.copiedText}`}>
                  {t('Vágólapra másolva!')}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
